import { useEffect, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { CustomUser, UserPermission } from 'api';
import { CompanyByFirm } from 'common/types/company';
import { User } from 'common/types/user';
import { getFirmUserPermissions } from 'services/hooks/useUserSelected';
import { getNumberValue } from 'utilities';
import { Logger } from 'utilities/logger';

// Helper function: Filters and maps permissions for a specific company
const mapCompanyPermissions = (companyId: number, firmUserPermissions: { user_permission_set: UserPermission[] }[]) =>
  firmUserPermissions
    .filter(permission =>
      permission.user_permission_set.some(
        (p: UserPermission) =>
          p.feature_object && p.feature_object.object_id === companyId && p.feature_object.object_type === 'Company'
      )
    )
    .flatMap(permission =>
      permission.user_permission_set.map((p: UserPermission) => ({
        feature_object: p.feature_object ?? { object_id: 0, object_type: '' },
        user_id: p.user_id ? Number(p.user_id) : 0,
      }))
    );

// Helper function: Builds the updated company object
const buildUpdatedCompany = (company: CompanyByFirm, permissions: { user_id: number }[], firmUsers: User[]) => {
  const usersInCompany = firmUsers.filter(user => permissions.some(permission => permission.user_id === user.id));

  return {
    ...company,
    users: usersInCompany,
    usersNotInCompany: firmUsers.filter(user => !permissions.some(permission => permission.user_id === user.id)),
  };
};

export const useCompanyPermissions = (firmId: number, filteredCompanies: CompanyByFirm[], firmUsers: CustomUser[]) => {
  const [loading, setLoading] = useState(true);
  const [companiesWithPermissions, setCompaniesWithPermissions] = useState(filteredCompanies);

  useEffect(() => {
    const fetchUserPermissions = async () => {
      setLoading(true);
      try {
        const firmUserPermissions = await getFirmUserPermissions(firmId);

        // Build a map of permissions for each company
        const companyPermissionsMap = filteredCompanies.reduce((acc, company) => {
          acc[getNumberValue(company.company_id)] = mapCompanyPermissions(getNumberValue(company.company_id), firmUserPermissions);
          return acc;
        }, {} as Record<number, { user_id: number }[]>);

        // Update each company with its users and users not in the company
        const tmpUpdatedCompanies = filteredCompanies.map(company =>
          buildUpdatedCompany(company, companyPermissionsMap[getNumberValue(company.company_id)] || [], firmUserPermissions)
        );

        setCompaniesWithPermissions(tmpUpdatedCompanies);
      } catch (error) {
        enqueueSnackbar(`Error fetching user permissions for firm ID ${firmId}`, { variant: 'error' });
        Logger.error(`Error fetching user permissions for firm ID ${firmId}`, error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserPermissions();
  }, [firmUsers, firmId, filteredCompanies]);

  return { companiesWithPermissions, loading };
};
